import React, { useEffect, useMemo, useState } from 'react';
import { DialogTitle, DialogContent, DialogActions, Dialog, Button, Stepper, Step as MuiStep, StepLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { WORKFLOW_TYPE, chipTypes, statusSubCampaign } from '@/helpers/constants';
import { getAvailableComment } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import campaignServices from '@/services/campaignServices';
import { PlantDialogWorkflow, PlantDialogStep, PlanStatus } from '@/types';
import InputText from '@/components/UI/InputText/InputText';
import Spinner from '@/components/UI/Spinner/Spinner';
import UniversalChip from '@/components/UI/UniversalChip/UniversalChip';
import StatusDialogPlantSection from './StatusDialogPlantSection';
import styles from './DialogPlantWorkflow.module.scss';

type DialogPlantWorkflowComponent = {
  close: () => void;
  subCRId: string;
  plant: PlanStatus;
  headerLanguage: string;
  isVANCR: boolean;
};

const DialogPlantWorkflow: React.FC<DialogPlantWorkflowComponent> = (props) => {
  const { close, subCRId, plant, headerLanguage, isVANCR } = props;
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const [plantWorkflow, setPlantWorkflow] = useState<PlantDialogWorkflow | null>(null);
  const [stepsContent, setStepsContent] = useState<PlantDialogStep[]>([]);
  const isDigitalWorkflow = plantWorkflow?.workflowType === WORKFLOW_TYPE.DIGITAL;

  useEffect(() => {
    campaignServices.getAdaptedSubCRWorkflow(subCRId, plant?.version)
      .then((res) => {
        setPlantWorkflow(res);
        setStepsContent(res.statuses);
      })
      .catch((err) => { addToast(err.message, 'ERROR'); close(); });
  }, [subCRId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = !plantWorkflow;

  const getStepLabel = (stepName: string) => {
    if ([statusSubCampaign.ACCEPTED, statusSubCampaign.DENIED].includes(stepName)) {
      return `${t('subCampaign.ACCEPTED')}/${t('subCampaign.DENIED')}`;
    }

    return t(`subCampaign.${stepName}`);
  };

  const showInPlantApprovalSection = useMemo(() => (stepsContent.some(({ name }) => name === statusSubCampaign.IN_E3_APPROVAL)), [stepsContent]);

  const showInConsolidationSection = useMemo(() => (
    stepsContent.some(({ name }) => name === statusSubCampaign.IN_CONSOLIDATION)
    && isDigitalWorkflow), [stepsContent, isDigitalWorkflow]);

  return (
    <Dialog
      open
      maxWidth="md"
      onClose={close}
      aria-labelledby="form-dialog-status"
    >
      <DialogTitle id="form-dialog-status-title">
        <h3 className={styles.DialogPlantTitle}>{`${t('workflow.plantDecisionDialog')} W${plant?.wkzNumber} - ${plant?.plantName}`}</h3>
      </DialogTitle>
      <DialogContent>
        <div className={styles.DialogStatus}>
          {isLoading && <Spinner isVisible isMask />}
          {!isLoading && (
            <>
              <h3 className={styles.DialogPlantSectionTitle}>
                {t('workflow.plantStatus')}
                <UniversalChip
                  chipType={chipTypes.WITHOUT_FUNCTION}
                  text={isVANCR ? 'VAN' : 'CAR'}
                />
              </h3>
              <Stepper alternativeLabel>
                {stepsContent?.map((step: PlantDialogStep) => (
                  <MuiStep
                    key={step.name}
                    completed={!step?.split && step.completed}
                    active={step?.current}
                  >
                    <StepLabel>{getStepLabel(step.name)}</StepLabel>
                  </MuiStep>
                ))}
              </Stepper>
              <div className={styles.Section}>
                {!stepsContent.find((step) => step?.split) && (isDigitalWorkflow && !isVANCR) ? (
                  <>
                    <StatusDialogPlantSection
                      plantWorkflow={plantWorkflow}
                      title={t(`subCampaign.${statusSubCampaign.IN_CHECKING}`)}
                      headerLanguage={headerLanguage}
                      statusKey="inChecking"
                      labelComments={() => t('dialog.statusCommentFromCampaignManager')}
                    />
                    {showInConsolidationSection && (
                      <>
                        <h3 className={styles.DialogPlantSectionTitle}>{t(`subCampaign.${statusSubCampaign.IN_CONSOLIDATION}`)}</h3>
                        {plantWorkflow?.inConsolidation?.comments?.map((comment) => (
                          <div className={styles.OneColumns}>
                            <InputText
                              id="input-status-comments-cc"
                              label={`${t('dialog.statusCommentFromCC')}`}
                              multiline
                              rows={4}
                              value={getAvailableComment(comment.commentValue, headerLanguage)}
                              disabled
                            />
                          </div>
                        ))}
                      </>
                    )}
                  </>
                ) : (!isVANCR && isDigitalWorkflow && (
                  <>
                    <StatusDialogPlantSection
                      plantWorkflow={plantWorkflow}
                      title={t(`subCampaign.${statusSubCampaign.IN_ANALYSIS}`)}
                      headerLanguage={headerLanguage}
                      statusKey="inValidation"
                      labelComments={() => t('dialog.statusCommentFromCampaignManager')}
                    />
                  </>
                ))}
              </div>
              {(showInPlantApprovalSection && !isVANCR) && (
                <>
                  <StatusDialogPlantSection
                    plantWorkflow={plantWorkflow}
                    title={t(`subCampaign.${statusSubCampaign.IN_E3_APPROVAL}`)}
                    headerLanguage={headerLanguage}
                    statusKey="inApprovalE3"
                    showActionTakenBy
                    showVersion={false}
                    labelComments={(comment) => `${t('dialog.statusCommentFromQMBPlantApprover')} - ${t(`workflow.plantComments.${comment?.commentLabel}`)}`}
                  />
                </>
              )}
              {isVANCR && (
                <>
                  <StatusDialogPlantSection
                    plantWorkflow={plantWorkflow}
                    title={t(`subCampaign.${statusSubCampaign.IN_PLANT_APPROVAL}`)}
                    headerLanguage={headerLanguage}
                    statusKey="inPlantApproval"
                    showActionTakenBy
                    showVersion
                    labelComments={(comment) => `${t('dialog.statusCommentFromApprover')} - ${t(`workflow.plantComments.${comment?.commentLabel}`)}`}
                  />
                  <StatusDialogPlantSection
                    plantWorkflow={plantWorkflow}
                    title={t(`subCampaign.${statusSubCampaign.IN_PLANT_COORDINATION}`)}
                    headerLanguage={headerLanguage}
                    statusKey="inPlantCoordination"
                    showActionTakenBy
                    showVersion
                    labelComments={(comment) => `${t('dialog.statusCommentFromApprover')} - ${t(`workflow.plantComments.${comment?.commentLabel}`)}`}
                  />
                </>
              )}
            </>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={close}>{t('dialog.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogPlantWorkflow;
